/**
 * AImry Assistant - AI Chat Component using Assistant UI
 *
 * Uses @assistant-ui/react with Vercel AI SDK for proper
 * streaming, tool calling, and AI chat UX.
 */

import React, { useState, useMemo, useEffect, useRef, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import { gsap } from 'gsap';
import { useOrbShimmer } from '@/shared/hooks/useOrbShimmer';
import { useAImryBubble } from '@/contexts/AImryBubbleContext';
import {
  AssistantRuntimeProvider,
  ThreadPrimitive,
  ComposerPrimitive,
  MessagePrimitive,
  makeAssistantToolUI,
  useThreadRuntime,
  useMessage,
} from '@assistant-ui/react';
import { useChatRuntime, AssistantChatTransport } from '@assistant-ui/react-ai-sdk';
import { motion, AnimatePresence } from 'framer-motion';
import { XIcon, SendIcon, CheckCircle, XCircle, Loader2, Calendar, Bug, Building2, Clock, HelpCircle, Sparkles, FileText, Search, ThumbsUp, ThumbsDown, BookOpen } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useRTL } from '@/shared/hooks/useRTL';
import useMediaQuery, { MOBILE_BREAKPOINT } from '@/shared/hooks/useMediaQuery';
import { useLayout } from '@/contexts/LayoutContext';
import { useAuthState } from '@/features/auth';
import { UserAvatar } from '@/shared/components/UserAvatar';
import { AImryStaticAvatarLazy as AImryStaticAvatar } from './AImry3DAvatarLazy';
import { getApiUrl } from '@/config/api.config';

// Chamama color palette
const COLORS = {
  yellow: '#F4D35E',
  lightBlue: '#CBEDFF',
  tan: '#DBC299',
  pink: '#FFB6F3',
  black: '#000000',
};

// =============================================================================
// TYPING INDICATOR - Bouncing dots animation for "thinking" state
// =============================================================================
const TypingIndicator: React.FC = () => {
  return (
    <div className="aimry-typing-indicator">
      <span className="dot" />
      <span className="dot" />
      <span className="dot" />
    </div>
  );
};

// Custom Text component that replaces "●" with TypingIndicator
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const CustomText: React.FC<any> = (props) => {
  // Debug: log props to understand the structure
  console.log('🔍 CustomText props:', props);

  // Try different ways to get the text content
  const text = props?.part?.text || props?.text || props?.children || '';

  // Check if this text is the in-progress indicator
  if (text === '●' || (typeof text === 'string' && text.trim() === '●')) {
    return <TypingIndicator />;
  }

  // Remove trailing "●" from streaming text
  const cleanText = typeof text === 'string' ? text.replace(/●$/, '') : text;
  return <span>{cleanText}</span>;
};

// Custom assistant content component with custom renderers
// Shows typing indicator when message content is empty (streaming just started)
const AssistantMessageContent: React.FC = () => {
  const message = useMessage();

  // Check if message has any text content
  const hasTextContent = message?.content?.some(
    (part: { type: string; text?: string }) =>
      part.type === 'text' && part.text && part.text.trim().length > 0 && part.text !== '●'
  );

  // Show typing indicator when message is empty or only has the bullet indicator
  if (!hasTextContent) {
    return <TypingIndicator />;
  }

  return (
    <MessagePrimitive.Content
      components={{
        Text: CustomText,
      }}
    />
  );
};

// Tool UI component for createBooking - Shows a nice booking card
const CreateBookingToolUI = makeAssistantToolUI<
  { roomId: string; date: string; startTime: string; endTime: string; note?: string },
  { success: boolean; message?: string; error?: string; bookingId?: string; roomName?: string; booking?: { date: string; startTime: string; endTime: string; room: string; status: string } }
>({
  toolName: 'createBooking',
  render: ({ args, result, status }) => {
    const isLoading = status.type === 'running';
    const isSuccess = result?.success;
    const isError = result && !result.success;

    // Extract booking details - prefer result.booking (from backend), fallback to args
    const bookingDate = result?.booking?.date || args?.date || '';
    const bookingStartTime = result?.booking?.startTime || args?.startTime || '';
    const bookingEndTime = result?.booking?.endTime || args?.endTime || '';
    const roomName = result?.roomName || result?.booking?.room || '';

    // Loading state - minimal
    if (isLoading) {
      return (
        <div
          className="my-2 p-3 rounded-lg text-sm"
          style={{
            backgroundColor: 'var(--theme-card-cyan)',
            border: '2px solid var(--theme-border)',
          }}
        >
          <div className="flex items-center gap-2 font-bold">
            <Loader2 className="w-4 h-4 animate-spin" />
            <span>📅 Creating Booking...</span>
          </div>
        </div>
      );
    }

    // Success state - Nice booking confirmation card
    if (isSuccess) {
      return (
        <div
          className="my-2 p-4 rounded-lg text-sm"
          style={{
            backgroundColor: '#d1fae5',
            border: '3px solid #059669',
            boxShadow: '4px 4px 0px #059669',
          }}
        >
          <div className="flex items-center gap-2 font-bold text-green-800 mb-3">
            <CheckCircle className="w-5 h-5" />
            <span className="text-base">Booking Confirmed! 🎉</span>
          </div>
          <div className="space-y-2 text-green-900">
            {bookingDate && (
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                <span className="font-medium">{bookingDate}</span>
              </div>
            )}
            {(bookingStartTime || bookingEndTime) && (
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4" />
                <span>{bookingStartTime} - {bookingEndTime}</span>
              </div>
            )}
            {roomName && (
              <div className="flex items-center gap-2">
                <Building2 className="w-4 h-4" />
                <span>{roomName}</span>
              </div>
            )}
          </div>
          {result.bookingId && (
            <div className="mt-3 pt-2 border-t border-green-300 text-xs text-green-700">
              Booking ID: {result.bookingId.slice(0, 8)}...
            </div>
          )}
        </div>
      );
    }

    // Error state
    if (isError) {
      return (
        <div
          className="my-2 p-4 rounded-lg text-sm"
          style={{
            backgroundColor: '#fee2e2',
            border: '3px solid #dc2626',
            boxShadow: '4px 4px 0px #dc2626',
          }}
        >
          <div className="flex items-center gap-2 font-bold text-red-800 mb-2">
            <XCircle className="w-5 h-5" />
            <span>Booking Failed</span>
          </div>
          <div className="text-red-700 text-sm">
            {result?.error || 'Unable to create booking. Please try again.'}
          </div>
        </div>
      );
    }

    return null;
  },
});

// Tool UI component for reportIncident
const ReportIncidentToolUI = makeAssistantToolUI<
  { title: string; description: string; category: string },
  { success: boolean; message?: string; error?: string; incidentId?: string }
>({
  toolName: 'reportIncident',
  render: ({ args, result, status }) => {
    const isLoading = status.type === 'running';
    const isSuccess = result?.success;
    const isError = result && !result.success;

    return (
      <div
        className="my-2 p-3 rounded-lg text-sm"
        style={{
          backgroundColor: isSuccess ? 'var(--theme-card-green, #d1fae5)' : isError ? 'var(--theme-card-red, #fee2e2)' : 'var(--theme-card-cyan)',
          border: '2px solid var(--theme-border)',
        }}
      >
        <div className="flex items-center gap-2 font-bold mb-1">
          {isLoading ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : isSuccess ? (
            <CheckCircle className="w-4 h-4 text-green-600" />
          ) : isError ? (
            <XCircle className="w-4 h-4 text-red-600" />
          ) : (
            <Bug className="w-4 h-4" />
          )}
          <span>🐛 Reporting Issue...</span>
        </div>
        <div className="text-xs opacity-80">
          [{args.category}] {args.title}
        </div>
        {result && (
          <div className="mt-2 text-xs font-medium">
            {isSuccess ? (
              <span className="text-green-700">✅ {result.message}</span>
            ) : (
              <span className="text-red-700">❌ {result.error}</span>
            )}
          </div>
        )}
      </div>
    );
  },
});

// Tool UI component for getRooms - Shows clickable room buttons
const GetRoomsToolUI = makeAssistantToolUI<
  Record<string, never>,
  { rooms: Array<{ id: string; name: string; type: string; capacity: number }> }
>({
  toolName: 'getRooms',
  render: function GetRoomsRender({ result, status }) {
    const threadRuntime = useThreadRuntime();
    const [selectedRoom, setSelectedRoom] = React.useState<string | null>(null);
    const isLoading = status.type === 'running';

    // Handle both direct result and nested result (output.value wrapper)
    const resultData = (result as { value?: { rooms?: Array<{ id: string; name: string; type: string; capacity: number }> } })?.value || result;
    const rooms = resultData?.rooms || [];

    const handleSelectRoom = (room: { id: string; name: string }) => {
      if (selectedRoom) return; // Prevent double-click
      setSelectedRoom(room.id);
      // Send selection message to AI
      threadRuntime.append({
        role: 'user',
        content: [{ type: 'text', text: `I want to book "${room.name}" (ID: ${room.id})` }],
      });
    };

    // Loading state
    if (isLoading) {
      return (
        <div
          className="my-2 p-3 rounded-lg text-sm"
          style={{
            backgroundColor: 'var(--theme-card-cyan)',
            border: '2px solid var(--theme-border)',
          }}
        >
          <div className="flex items-center gap-2 font-bold">
            <Loader2 className="w-4 h-4 animate-spin" />
            <span>🏠 Fetching Rooms...</span>
          </div>
        </div>
      );
    }

    // Show room selection buttons
    if (rooms.length > 0) {
      return (
        <div
          className="my-2 p-4 rounded-lg text-sm"
          style={{
            backgroundColor: 'var(--theme-card-green)',
            border: '3px solid var(--theme-border)',
            boxShadow: '4px 4px 0px var(--theme-border)',
          }}
        >
          <div className="font-bold mb-3 flex items-center gap-2" style={{ color: 'var(--theme-text)' }}>
            <Building2 className="w-5 h-5" />
            <span>Select a Room:</span>
          </div>

          <div className="grid gap-2">
            {rooms.map((room) => (
              <button
                key={room.id}
                onClick={() => handleSelectRoom(room)}
                disabled={!!selectedRoom}
                className={`w-full flex items-center justify-between p-3 rounded-lg font-medium transition-all ${
                  selectedRoom === room.id
                    ? 'scale-95 opacity-70'
                    : selectedRoom
                      ? 'opacity-50 cursor-not-allowed'
                      : 'hover:scale-102 active:scale-98'
                }`}
                style={{
                  backgroundColor: selectedRoom === room.id ? '#059669' : 'var(--theme-card-cyan)',
                  color: selectedRoom === room.id ? 'white' : 'var(--theme-text)',
                  border: '2px solid var(--theme-border)',
                  boxShadow: selectedRoom === room.id ? 'none' : '2px 2px 0px var(--theme-border)',
                }}
              >
                <div className="flex items-center gap-2">
                  <Building2 className="w-4 h-4" />
                  <span>{room.name}</span>
                </div>
                <div className="text-xs opacity-75">
                  {room.type} • {room.capacity} people
                </div>
              </button>
            ))}
          </div>

          {selectedRoom && (
            <div className="mt-3 flex items-center justify-center gap-2 text-sm" style={{ color: '#059669' }}>
              <CheckCircle className="w-4 h-4" />
              <span>Room selected!</span>
            </div>
          )}
        </div>
      );
    }

    return null;
  },
});

// ====== WIZARD STEP 2: Available Days Selection ======
const GetAvailableDaysToolUI = makeAssistantToolUI<
  { room_id?: string; roomId?: string; roomID?: string },
  {
    success: boolean;
    error?: string;
    roomId?: string;
    roomName?: string;
    days?: Array<{ date: string; dayName: string; shortDate: string; label: string; isToday?: boolean; isTomorrow?: boolean }>;
    message?: string;
  }
>({
  toolName: 'getAvailableDays',
  render: function GetAvailableDaysRender({ result, status }) {
    const threadRuntime = useThreadRuntime();
    const [selectedDay, setSelectedDay] = React.useState<string | null>(null);
    const isLoading = status.type === 'running';

    type DaysResult = {
      success: boolean;
      error?: string;
      roomId?: string;
      roomName?: string;
      days?: Array<{ date: string; dayName: string; shortDate: string; label: string; isToday?: boolean; isTomorrow?: boolean }>;
    };
    const resultData = ((result as { value?: DaysResult })?.value || result) as DaysResult | undefined;
    const isError = resultData && !resultData.success;
    const days = resultData?.days || [];

    const handleSelectDay = (day: { date: string; dayName: string }) => {
      if (selectedDay) return;
      setSelectedDay(day.date);
      threadRuntime.append({
        role: 'user',
        content: [{ type: 'text', text: `I want to book on ${day.date} (${day.dayName})` }],
      });
    };

    if (isLoading) {
      return (
        <div className="my-2 p-3 rounded-lg text-sm" style={{ backgroundColor: 'var(--theme-card-cyan)', border: '2px solid var(--theme-border)' }}>
          <div className="flex items-center gap-2 font-bold">
            <Loader2 className="w-4 h-4 animate-spin" />
            <span>📅 Loading Available Days...</span>
          </div>
        </div>
      );
    }

    if (isError) {
      return (
        <div className="my-2 p-4 rounded-lg text-sm" style={{ backgroundColor: '#fee2e2', border: '3px solid #dc2626', boxShadow: '4px 4px 0px #dc2626' }}>
          <div className="flex items-center gap-2 font-bold text-red-800 mb-2">
            <XCircle className="w-5 h-5" />
            <span>Cannot Load Days</span>
          </div>
          <div className="text-red-700 text-sm">{resultData?.error}</div>
        </div>
      );
    }

    if (days.length > 0) {
      return (
        <div className="my-2 p-4 rounded-lg text-sm" style={{ backgroundColor: 'var(--theme-card-cyan)', border: '3px solid var(--theme-border)', boxShadow: '4px 4px 0px var(--theme-border)' }}>
          <div className="font-bold mb-2 flex items-center gap-2" style={{ color: 'var(--theme-text)' }}>
            <Calendar className="w-5 h-5" />
            <span>Select a Day:</span>
          </div>
          {resultData?.roomName && (
            <div className="text-xs mb-3 opacity-75" style={{ color: 'var(--theme-text-muted)' }}>
              Booking: {resultData.roomName}
            </div>
          )}
          <div className="grid grid-cols-2 gap-2 max-h-60 overflow-y-auto">
            {days.slice(0, 14).map((day) => (
              <button
                key={day.date}
                onClick={() => handleSelectDay(day)}
                disabled={!!selectedDay}
                className={`flex items-center justify-center p-2 rounded-lg font-medium text-sm transition-all ${
                  selectedDay === day.date ? 'scale-95 opacity-70' : selectedDay ? 'opacity-50 cursor-not-allowed' : 'hover:scale-102 active:scale-98'
                }`}
                style={{
                  backgroundColor: selectedDay === day.date ? '#059669' : day.isToday ? 'var(--theme-card-green)' : day.isTomorrow ? 'var(--theme-card-yellow)' : 'var(--theme-card-pink)',
                  color: selectedDay === day.date ? 'white' : 'var(--theme-text)',
                  border: '2px solid var(--theme-border)',
                  boxShadow: selectedDay === day.date ? 'none' : '2px 2px 0px var(--theme-border)',
                }}
              >
                <Calendar className="w-3 h-3 mr-1" />
                {day.label}
              </button>
            ))}
          </div>
          {selectedDay && (
            <div className="mt-3 flex items-center justify-center gap-2 text-sm" style={{ color: '#059669' }}>
              <CheckCircle className="w-4 h-4" />
              <span>Day selected!</span>
            </div>
          )}
        </div>
      );
    }
    return null;
  },
});

// ====== WIZARD STEP 3: Available Start Times Selection ======
const GetAvailableStartTimesToolUI = makeAssistantToolUI<
  { room_id?: string; roomId?: string; roomID?: string; date: string },
  {
    success: boolean;
    error?: string;
    roomId?: string;
    roomName?: string;
    date?: string;
    dayName?: string;
    startTimes?: Array<{ time: string; label: string; available: boolean }>;
    message?: string;
  }
>({
  toolName: 'getAvailableStartTimes',
  render: function GetStartTimesRender({ result, status }) {
    const threadRuntime = useThreadRuntime();
    const [selectedTime, setSelectedTime] = React.useState<string | null>(null);
    const isLoading = status.type === 'running';

    type StartTimesResult = {
      success: boolean;
      error?: string;
      roomId?: string;
      roomName?: string;
      date?: string;
      dayName?: string;
      startTimes?: Array<{ time: string; label: string; available: boolean }>;
    };
    const resultData = ((result as { value?: StartTimesResult })?.value || result) as StartTimesResult | undefined;
    const isError = resultData && !resultData.success;
    const startTimes = resultData?.startTimes || [];

    const handleSelectTime = (time: string) => {
      if (selectedTime) return;
      setSelectedTime(time);
      threadRuntime.append({
        role: 'user',
        content: [{ type: 'text', text: `I want to start at ${time}` }],
      });
    };

    if (isLoading) {
      return (
        <div className="my-2 p-3 rounded-lg text-sm" style={{ backgroundColor: 'var(--theme-card-cyan)', border: '2px solid var(--theme-border)' }}>
          <div className="flex items-center gap-2 font-bold">
            <Loader2 className="w-4 h-4 animate-spin" />
            <span>⏰ Loading Start Times...</span>
          </div>
        </div>
      );
    }

    if (isError) {
      return (
        <div className="my-2 p-4 rounded-lg text-sm" style={{ backgroundColor: '#fee2e2', border: '3px solid #dc2626', boxShadow: '4px 4px 0px #dc2626' }}>
          <div className="flex items-center gap-2 font-bold text-red-800 mb-2">
            <XCircle className="w-5 h-5" />
            <span>Cannot Load Times</span>
          </div>
          <div className="text-red-700 text-sm">{resultData?.error}</div>
        </div>
      );
    }

    if (startTimes.length > 0) {
      return (
        <div className="my-2 p-4 rounded-lg text-sm" style={{ backgroundColor: 'var(--theme-card-cyan)', border: '3px solid var(--theme-border)', boxShadow: '4px 4px 0px var(--theme-border)' }}>
          <div className="font-bold mb-2 flex items-center gap-2" style={{ color: 'var(--theme-text)' }}>
            <Clock className="w-5 h-5" />
            <span>Select Starting Time:</span>
          </div>
          {resultData?.roomName && resultData?.dayName && (
            <div className="text-xs mb-3 opacity-75" style={{ color: 'var(--theme-text-muted)' }}>
              {resultData.roomName} • {resultData.dayName}, {resultData.date}
            </div>
          )}
          <div className="grid grid-cols-3 gap-2">
            {startTimes.map((slot) => (
              <button
                key={slot.time}
                onClick={() => handleSelectTime(slot.time)}
                disabled={!!selectedTime}
                className={`flex items-center justify-center p-2 rounded-lg font-medium text-sm transition-all ${
                  selectedTime === slot.time ? 'scale-95 opacity-70' : selectedTime ? 'opacity-50 cursor-not-allowed' : 'hover:scale-102 active:scale-98'
                }`}
                style={{
                  backgroundColor: selectedTime === slot.time ? '#059669' : 'var(--theme-card-green)',
                  color: selectedTime === slot.time ? 'white' : 'var(--theme-text)',
                  border: '2px solid var(--theme-border)',
                  boxShadow: selectedTime === slot.time ? 'none' : '2px 2px 0px var(--theme-border)',
                }}
              >
                <Clock className="w-3 h-3 mr-1" />
                {slot.label}
              </button>
            ))}
          </div>
          {selectedTime && (
            <div className="mt-3 flex items-center justify-center gap-2 text-sm" style={{ color: '#059669' }}>
              <CheckCircle className="w-4 h-4" />
              <span>Start time selected!</span>
            </div>
          )}
        </div>
      );
    }
    return null;
  },
});

// ====== WIZARD STEP 4: Available Durations Selection ======
const GetAvailableDurationsToolUI = makeAssistantToolUI<
  { room_id?: string; roomId?: string; roomID?: string; date: string; start_time?: string; startTime?: string },
  {
    success: boolean;
    error?: string;
    roomId?: string;
    roomName?: string;
    date?: string;
    dayName?: string;
    startTime?: string;
    durations?: Array<{ hours: number; minutes: number; endTime: string; label: string }>;
    maxDurationHours?: number;
    message?: string;
  }
>({
  toolName: 'getAvailableDurations',
  render: function GetDurationsRender({ result, status }) {
    const threadRuntime = useThreadRuntime();
    const [selectedDuration, setSelectedDuration] = React.useState<number | null>(null);
    const isLoading = status.type === 'running';

    type DurationsResult = {
      success: boolean;
      error?: string;
      roomId?: string;
      roomName?: string;
      date?: string;
      dayName?: string;
      startTime?: string;
      durations?: Array<{ hours: number; minutes: number; endTime: string; label: string }>;
      maxDurationHours?: number;
    };
    const resultData = ((result as { value?: DurationsResult })?.value || result) as DurationsResult | undefined;
    const isError = resultData && !resultData.success;
    const durations = resultData?.durations || [];

    const handleSelectDuration = (duration: { hours: number; endTime: string; label: string }) => {
      if (selectedDuration !== null) return;
      setSelectedDuration(duration.hours);
      threadRuntime.append({
        role: 'user',
        content: [{ type: 'text', text: `I want to book for ${duration.label} (until ${duration.endTime})` }],
      });
    };

    if (isLoading) {
      return (
        <div className="my-2 p-3 rounded-lg text-sm" style={{ backgroundColor: 'var(--theme-card-cyan)', border: '2px solid var(--theme-border)' }}>
          <div className="flex items-center gap-2 font-bold">
            <Loader2 className="w-4 h-4 animate-spin" />
            <span>⏱️ Loading Durations...</span>
          </div>
        </div>
      );
    }

    if (isError) {
      return (
        <div className="my-2 p-4 rounded-lg text-sm" style={{ backgroundColor: '#fee2e2', border: '3px solid #dc2626', boxShadow: '4px 4px 0px #dc2626' }}>
          <div className="flex items-center gap-2 font-bold text-red-800 mb-2">
            <XCircle className="w-5 h-5" />
            <span>Cannot Load Durations</span>
          </div>
          <div className="text-red-700 text-sm">{resultData?.error}</div>
        </div>
      );
    }

    if (durations.length > 0) {
      return (
        <div className="my-2 p-4 rounded-lg text-sm" style={{ backgroundColor: 'var(--theme-card-cyan)', border: '3px solid var(--theme-border)', boxShadow: '4px 4px 0px var(--theme-border)' }}>
          <div className="font-bold mb-2 flex items-center gap-2" style={{ color: 'var(--theme-text)' }}>
            <Clock className="w-5 h-5" />
            <span>Select Duration:</span>
          </div>
          {resultData?.roomName && resultData?.startTime && (
            <div className="text-xs mb-3 opacity-75" style={{ color: 'var(--theme-text-muted)' }}>
              {resultData.roomName} • Starting at {resultData.startTime} on {resultData.dayName}
            </div>
          )}
          <div className="grid grid-cols-2 gap-2">
            {durations.map((dur) => (
              <button
                key={dur.hours}
                onClick={() => handleSelectDuration(dur)}
                disabled={selectedDuration !== null}
                className={`flex flex-col items-center justify-center p-3 rounded-lg font-medium text-sm transition-all ${
                  selectedDuration === dur.hours ? 'scale-95 opacity-70' : selectedDuration !== null ? 'opacity-50 cursor-not-allowed' : 'hover:scale-102 active:scale-98'
                }`}
                style={{
                  backgroundColor: selectedDuration === dur.hours ? '#059669' : 'var(--theme-card-green)',
                  color: selectedDuration === dur.hours ? 'white' : 'var(--theme-text)',
                  border: '2px solid var(--theme-border)',
                  boxShadow: selectedDuration === dur.hours ? 'none' : '2px 2px 0px var(--theme-border)',
                }}
              >
                <span className="font-bold">{dur.label}</span>
                <span className="text-xs opacity-75">until {dur.endTime}</span>
              </button>
            ))}
          </div>
          {selectedDuration !== null && (
            <div className="mt-3 flex items-center justify-center gap-2 text-sm" style={{ color: '#059669' }}>
              <CheckCircle className="w-4 h-4" />
              <span>Duration selected!</span>
            </div>
          )}
        </div>
      );
    }
    return null;
  },
});

// ====== WIZARD STEP 5: Collect Booking Note (Required) ======
const CollectBookingNoteToolUI = makeAssistantToolUI<
  { room_id?: string; roomId?: string; room_name?: string; roomName?: string; date?: string; start_time?: string; startTime?: string; end_time?: string; endTime?: string },
  {
    success: boolean;
    error?: string;
    roomId?: string;
    roomName?: string;
    date?: string;
    dayName?: string;
    startTime?: string;
    endTime?: string;
    message?: string;
  }
>({
  toolName: 'collectBookingNote',
  render: function CollectNoteRender({ result, status }) {
    const { t } = useTranslation('common');
    const threadRuntime = useThreadRuntime();
    const [note, setNote] = React.useState('');
    const [submitted, setSubmitted] = React.useState(false);
    const [error, setError] = React.useState<string | null>(null);
    const isLoading = status.type === 'running';

    type NoteResult = {
      success: boolean;
      error?: string;
      roomId?: string;
      roomName?: string;
      date?: string;
      dayName?: string;
      startTime?: string;
      endTime?: string;
    };
    const resultData = ((result as { value?: NoteResult })?.value || result) as NoteResult | undefined;
    const isError = resultData && !resultData.success;

    const handleSubmitNote = () => {
      // Validate minimum 8 characters
      if (note.trim().length < 8) {
        setError(t('aimry.noteMinLength'));
        return;
      }
      setError(null);
      setSubmitted(true);
      threadRuntime.append({
        role: 'user',
        content: [{ type: 'text', text: `My booking note is: "${note.trim()}"` }],
      });
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        handleSubmitNote();
      }
    };

    if (isLoading) {
      return (
        <div className="my-2 p-3 rounded-lg text-sm" style={{ backgroundColor: 'var(--theme-card-cyan)', border: '2px solid var(--theme-border)' }}>
          <div className="flex items-center gap-2 font-bold">
            <Loader2 className="w-4 h-4 animate-spin" />
            <span>📝 Preparing note input...</span>
          </div>
        </div>
      );
    }

    if (isError) {
      return (
        <div className="my-2 p-4 rounded-lg text-sm" style={{ backgroundColor: '#fee2e2', border: '3px solid #dc2626', boxShadow: '4px 4px 0px #dc2626' }}>
          <div className="flex items-center gap-2 font-bold text-red-800 mb-2">
            <XCircle className="w-5 h-5" />
            <span>Error</span>
          </div>
          <div className="text-red-700 text-sm">{resultData?.error}</div>
        </div>
      );
    }

    return (
      <div className="my-2 p-4 rounded-lg text-sm" style={{ backgroundColor: 'var(--theme-card-green)', border: '3px solid var(--theme-border)', boxShadow: '4px 4px 0px var(--theme-border)' }}>
        <div className="font-bold mb-2 flex items-center gap-2" style={{ color: 'var(--theme-text)' }}>
          <FileText className="w-5 h-5" />
          <span>Add Booking Note (Required)</span>
        </div>
        {resultData?.roomName && resultData?.startTime && (
          <div className="text-xs mb-3 opacity-75" style={{ color: 'var(--theme-text-muted)' }}>
            {resultData.roomName} • {resultData.dayName}, {resultData.startTime} - {resultData.endTime}
          </div>
        )}

        {!submitted ? (
          <div className="space-y-3">
            <div className="text-xs opacity-75 mb-2" style={{ color: 'var(--theme-text-muted)' }}>
              Please describe the purpose of your booking (minimum 8 characters)
            </div>
            <textarea
              value={note}
              onChange={(e) => {
                setNote(e.target.value);
                if (error && e.target.value.trim().length >= 8) {
                  setError(null);
                }
              }}
              onKeyDown={handleKeyDown}
              placeholder="e.g., Team meeting to discuss Q1 planning..."
              className="w-full p-3 rounded-lg text-sm resize-none"
              style={{
                backgroundColor: 'white',
                color: 'var(--theme-text)',
                border: error ? '2px solid #dc2626' : '2px solid var(--theme-border)',
                minHeight: '80px',
              }}
              disabled={submitted}
              autoFocus
            />
            {error && (
              <div className="text-xs text-red-600 flex items-center gap-1">
                <XCircle className="w-3 h-3" />
                {error}
              </div>
            )}
            <div className="flex items-center justify-between">
              <span className="text-xs opacity-50" style={{ color: 'var(--theme-text-muted)' }}>
                {note.trim().length}/8 characters minimum
              </span>
              <button
                onClick={handleSubmitNote}
                disabled={submitted}
                className="flex items-center gap-2 px-4 py-2 rounded-lg font-bold text-sm transition-all hover:scale-105 active:scale-95"
                style={{
                  backgroundColor: note.trim().length >= 8 ? '#059669' : 'var(--theme-card-cyan)',
                  color: note.trim().length >= 8 ? 'white' : 'var(--theme-text)',
                  border: '2px solid var(--theme-border)',
                  boxShadow: '2px 2px 0px var(--theme-border)',
                  opacity: note.trim().length >= 8 ? 1 : 0.7,
                }}
              >
                <CheckCircle className="w-4 h-4" />
                Continue
              </button>
            </div>
          </div>
        ) : (
          <div className="flex items-center justify-center gap-2 text-sm" style={{ color: '#059669' }}>
            <CheckCircle className="w-4 h-4" />
            <span>Note added!</span>
          </div>
        )}
      </div>
    );
  },
});

// Tool UI component for proposeBooking - Shows confirmation card with Yes/No buttons
// Note: Gemini sends snake_case params, but result.proposal is camelCase from backend
const ProposeBookingToolUI = makeAssistantToolUI<
  { room_id: string; room_name: string; date: string; start_time: string; end_time: string; note?: string },
  {
    success: boolean;
    error?: string;
    proposal?: {
      roomId: string;
      roomName: string;
      date: string;
      startTime: string;
      endTime: string;
      note: string;
      dayName: string;
    };
    message?: string;
    awaitingConfirmation?: boolean;
  }
>({
  toolName: 'proposeBooking',
  render: function ProposeBookingRender({ args, result, status }) {
    const threadRuntime = useThreadRuntime();
    const [responded, setResponded] = React.useState(false);
    const isLoading = status.type === 'running';
    const isError = result && !result.success;
    const proposal = result?.proposal;

    const handleConfirm = () => {
      if (responded) return;
      setResponded(true);
      // Send confirmation message to AI
      threadRuntime.append({
        role: 'user',
        content: [{ type: 'text', text: 'Yes, please confirm this booking' }],
      });
    };

    const handleCancel = () => {
      if (responded) return;
      setResponded(true);
      // Send cancellation message to AI
      threadRuntime.append({
        role: 'user',
        content: [{ type: 'text', text: 'No, cancel this booking' }],
      });
    };

    // Loading state
    if (isLoading) {
      return (
        <div
          className="my-2 p-3 rounded-lg text-sm"
          style={{
            backgroundColor: 'var(--theme-card-cyan)',
            border: '2px solid var(--theme-border)',
          }}
        >
          <div className="flex items-center gap-2 font-bold">
            <Loader2 className="w-4 h-4 animate-spin" />
            <span>📋 Preparing booking...</span>
          </div>
        </div>
      );
    }

    // Error state
    if (isError) {
      return (
        <div
          className="my-2 p-4 rounded-lg text-sm"
          style={{
            backgroundColor: '#fee2e2',
            border: '3px solid #dc2626',
            boxShadow: '4px 4px 0px #dc2626',
          }}
        >
          <div className="flex items-center gap-2 font-bold text-red-800 mb-2">
            <XCircle className="w-5 h-5" />
            <span>Cannot Book</span>
          </div>
          <div className="text-red-700 text-sm">{result?.error}</div>
        </div>
      );
    }

    // Proposal confirmation card
    if (proposal) {
      return (
        <div
          className="my-2 p-4 rounded-lg text-sm"
          style={{
            backgroundColor: '#fef3c7',
            border: '3px solid #d97706',
            boxShadow: '4px 4px 0px #d97706',
          }}
        >
          <div className="flex items-center gap-2 font-bold text-amber-800 mb-3">
            <Calendar className="w-5 h-5" />
            <span className="text-base">Confirm Booking? 🎯</span>
          </div>
          <div className="space-y-2 text-amber-900 mb-4">
            <div className="flex items-center gap-2">
              <Building2 className="w-4 h-4" />
              <span className="font-medium">{proposal.roomName}</span>
            </div>
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4" />
              <span>{proposal.dayName}, {proposal.date}</span>
            </div>
            <div className="flex items-center gap-2">
              <Clock className="w-4 h-4" />
              <span>{proposal.startTime} - {proposal.endTime}</span>
            </div>
            {proposal.note && (
              <div className="text-xs opacity-75 mt-1">
                Note: {proposal.note}
              </div>
            )}
          </div>

          {/* Confirmation Buttons */}
          {!responded ? (
            <div className="flex gap-3">
              <button
                onClick={handleConfirm}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-lg font-bold text-white transition-all hover:scale-105 active:scale-95"
                style={{
                  backgroundColor: '#059669',
                  border: '2px solid #047857',
                  boxShadow: '3px 3px 0px #047857',
                }}
              >
                <CheckCircle className="w-4 h-4" />
                Yes, Book It!
              </button>
              <button
                onClick={handleCancel}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-lg font-bold text-white transition-all hover:scale-105 active:scale-95"
                style={{
                  backgroundColor: '#dc2626',
                  border: '2px solid #b91c1c',
                  boxShadow: '3px 3px 0px #b91c1c',
                }}
              >
                <XCircle className="w-4 h-4" />
                No, Cancel
              </button>
            </div>
          ) : (
            <div className="flex items-center justify-center gap-2 font-medium" style={{ color: '#059669' }}>
              <CheckCircle className="w-4 h-4" />
              <span>Response sent!</span>
            </div>
          )}
        </div>
      );
    }

    return null;
  },
});

// =============================================================================
// HELP WIZARD TOOL UIs
// =============================================================================

// Help topic type for getHelpTopics result
interface HelpTopic {
  id: string;
  name: string;
  icon: string;
  description: string;
}

// Help article type for getHelpArticles and searchHelpArticles results
interface HelpArticle {
  id: string;
  title: string;
  category?: string;
}

// Tool UI component for getHelpTopics - Shows help topic category buttons
const GetHelpTopicsToolUI = makeAssistantToolUI<
  Record<string, never>,
  { success: boolean; topics: HelpTopic[]; message: string }
>({
  toolName: 'getHelpTopics',
  render: function GetHelpTopicsRender({ result, status }) {
    const { t } = useTranslation('common');
    const threadRuntime = useThreadRuntime();
    const [selectedTopic, setSelectedTopic] = React.useState<string | null>(null);
    const isLoading = status.type === 'running';

    // Handle both direct result and nested result (output.value wrapper)
    const resultData = (result as { value?: { topics?: HelpTopic[] } })?.value || result;
    const topics = resultData?.topics || [];

    // Icon mapping for topics
    const iconMap: Record<string, React.ReactNode> = {
      calendar: <Calendar className="w-5 h-5" />,
      'calendar-days': <Calendar className="w-5 h-5" />,
      speaker: <Sparkles className="w-5 h-5" />,
      user: <FileText className="w-5 h-5" />,
      wrench: <Bug className="w-5 h-5" />,
      'help-circle': <HelpCircle className="w-5 h-5" />,
    };

    const handleSelectTopic = (topic: HelpTopic) => {
      if (selectedTopic) return; // Prevent double-click
      setSelectedTopic(topic.id);
      // Send selection message to AI
      threadRuntime.append({
        role: 'user',
        content: [{ type: 'text', text: `I need help with "${topic.name}" (topic: ${topic.id})` }],
      });
    };

    // Loading state
    if (isLoading) {
      return (
        <div
          className="my-2 p-3 rounded-lg text-sm"
          style={{
            backgroundColor: 'var(--theme-card-cyan)',
            border: '2px solid var(--theme-border)',
          }}
        >
          <div className="flex items-center gap-2 font-bold">
            <Loader2 className="w-4 h-4 animate-spin" />
            <span>{t('aimry.loadingHelpTopics')}</span>
          </div>
        </div>
      );
    }

    // Show topic selection buttons
    if (topics.length > 0) {
      return (
        <div
          className="my-2 p-4 rounded-lg text-sm"
          style={{
            backgroundColor: 'var(--theme-card-cyan)',
            border: '3px solid var(--theme-border)',
            boxShadow: '4px 4px 0px var(--theme-border)',
          }}
        >
          <div className="font-bold mb-3 flex items-center gap-2" style={{ color: 'var(--theme-text)' }}>
            <HelpCircle className="w-5 h-5" />
            <span>{t('aimry.howCanIHelp')}</span>
          </div>

          <div className="grid grid-cols-2 gap-2">
            {topics.map((topic) => (
              <button
                key={topic.id}
                onClick={() => handleSelectTopic(topic)}
                disabled={!!selectedTopic}
                className={`w-full flex flex-col items-center p-3 rounded-lg font-medium transition-all ${
                  selectedTopic === topic.id
                    ? 'scale-95 opacity-70'
                    : selectedTopic
                      ? 'opacity-50 cursor-not-allowed'
                      : 'hover:scale-102 active:scale-98'
                }`}
                style={{
                  backgroundColor: selectedTopic === topic.id ? '#0891b2' : 'var(--theme-card-yellow)',
                  color: selectedTopic === topic.id ? 'white' : 'var(--theme-text)',
                  border: '2px solid var(--theme-border)',
                  boxShadow: selectedTopic === topic.id ? 'none' : '2px 2px 0px var(--theme-border)',
                }}
              >
                <div className="mb-1">{iconMap[topic.icon] || <HelpCircle className="w-5 h-5" />}</div>
                <span className="text-sm font-bold">{topic.name}</span>
                <span className="text-xs opacity-75 mt-1 text-center">{topic.description}</span>
              </button>
            ))}
          </div>

          {selectedTopic && (
            <div className="mt-3 flex items-center justify-center gap-2 text-sm" style={{ color: '#0891b2' }}>
              <CheckCircle className="w-4 h-4" />
              <span>Topic selected!</span>
            </div>
          )}
        </div>
      );
    }

    return null;
  },
});

// Tool UI component for getHelpArticles - Shows articles for a selected topic
const GetHelpArticlesToolUI = makeAssistantToolUI<
  { topic_id?: string; topicId?: string },
  { success: boolean; topicId: string; topicName: string; articles: HelpArticle[]; message: string }
>({
  toolName: 'getHelpArticles',
  render: function GetHelpArticlesRender({ result, status }) {
    const threadRuntime = useThreadRuntime();
    const [selectedArticle, setSelectedArticle] = React.useState<string | null>(null);
    const isLoading = status.type === 'running';

    // Handle both direct result and nested result (output.value wrapper)
    const resultData = (result as { value?: { articles?: HelpArticle[]; topicName?: string } })?.value || result;
    const articles = resultData?.articles || [];
    const topicName = resultData?.topicName || 'Help';

    const handleSelectArticle = (article: HelpArticle) => {
      if (selectedArticle) return; // Prevent double-click
      setSelectedArticle(article.id);
      // Send selection message to AI
      threadRuntime.append({
        role: 'user',
        content: [{ type: 'text', text: `Show me article "${article.title}" (id: ${article.id})` }],
      });
    };

    const handleReportIssue = () => {
      threadRuntime.append({
        role: 'user',
        content: [{ type: 'text', text: 'I want to report an issue' }],
      });
    };

    // Loading state
    if (isLoading) {
      return (
        <div
          className="my-2 p-3 rounded-lg text-sm"
          style={{
            backgroundColor: 'var(--theme-card-green)',
            border: '2px solid var(--theme-border)',
          }}
        >
          <div className="flex items-center gap-2 font-bold">
            <Loader2 className="w-4 h-4 animate-spin" />
            <span>📖 Loading articles...</span>
          </div>
        </div>
      );
    }

    // Show articles or no articles message
    return (
      <div
        className="my-2 p-4 rounded-lg text-sm"
        style={{
          backgroundColor: 'var(--theme-card-green)',
          border: '3px solid var(--theme-border)',
          boxShadow: '4px 4px 0px var(--theme-border)',
        }}
      >
        <div className="font-bold mb-3 flex items-center gap-2" style={{ color: 'var(--theme-text)' }}>
          <FileText className="w-5 h-5" />
          <span>{topicName} Articles</span>
        </div>

        {articles.length > 0 ? (
          <div className="flex flex-col gap-2">
            {articles.map((article) => (
              <button
                key={article.id}
                onClick={() => handleSelectArticle(article)}
                disabled={!!selectedArticle}
                className={`w-full flex items-center gap-2 p-3 rounded-lg font-medium text-left transition-all ${
                  selectedArticle === article.id
                    ? 'scale-95 opacity-70'
                    : selectedArticle
                      ? 'opacity-50 cursor-not-allowed'
                      : 'hover:scale-102 active:scale-98'
                }`}
                style={{
                  backgroundColor: selectedArticle === article.id ? '#059669' : 'var(--theme-card-cyan)',
                  color: selectedArticle === article.id ? 'white' : 'var(--theme-text)',
                  border: '2px solid var(--theme-border)',
                  boxShadow: selectedArticle === article.id ? 'none' : '2px 2px 0px var(--theme-border)',
                }}
              >
                <BookOpen className="w-4 h-4 flex-shrink-0" />
                <span className="text-sm">{article.title}</span>
              </button>
            ))}
          </div>
        ) : (
          <div className="text-center py-4">
            <div className="opacity-75 mb-3">No articles found for this topic.</div>
            <button
              onClick={handleReportIssue}
              className="px-4 py-2 rounded-lg font-bold transition-all hover:scale-105 active:scale-95"
              style={{
                backgroundColor: 'var(--theme-card-yellow)',
                color: 'var(--theme-text)',
                border: '2px solid var(--theme-border)',
                boxShadow: '2px 2px 0px var(--theme-border)',
              }}
            >
              <Bug className="w-4 h-4 inline mr-2" />
              Report an Issue
            </button>
          </div>
        )}

        {selectedArticle && (
          <div className="mt-3 flex items-center justify-center gap-2 text-sm" style={{ color: '#059669' }}>
            <CheckCircle className="w-4 h-4" />
            <span>Article selected!</span>
          </div>
        )}
      </div>
    );
  },
});

// Tool UI component for showHelpArticle - Displays full article content with feedback buttons
const ShowHelpArticleToolUI = makeAssistantToolUI<
  { article_id?: string; articleId?: string },
  {
    success: boolean;
    article: {
      id: string;
      title: string;
      content: string;
      category: string;
    };
    message: string;
  }
>({
  toolName: 'showHelpArticle',
  render: function ShowHelpArticleRender({ result, status }) {
    const threadRuntime = useThreadRuntime();
    const [responded, setResponded] = React.useState(false);
    const isLoading = status.type === 'running';

    // Handle both direct result and nested result (output.value wrapper)
    const resultData = (result as { value?: { article?: { id: string; title: string; content: string; category: string } } })?.value || result;
    const article = resultData?.article;

    const handleHelpful = () => {
      if (responded) return;
      setResponded(true);
      threadRuntime.append({
        role: 'user',
        content: [{ type: 'text', text: 'This helped! Thank you.' }],
      });
    };

    const handleNotHelpful = () => {
      if (responded) return;
      setResponded(true);
      threadRuntime.append({
        role: 'user',
        content: [{ type: 'text', text: "Still need help - this didn't solve my issue" }],
      });
    };

    // Loading state
    if (isLoading) {
      return (
        <div
          className="my-2 p-3 rounded-lg text-sm"
          style={{
            backgroundColor: 'var(--theme-card-yellow)',
            border: '2px solid var(--theme-border)',
          }}
        >
          <div className="flex items-center gap-2 font-bold">
            <Loader2 className="w-4 h-4 animate-spin" />
            <span>📄 Loading article...</span>
          </div>
        </div>
      );
    }

    // Show article content
    if (article) {
      return (
        <div
          className="my-2 p-4 rounded-lg text-sm"
          style={{
            backgroundColor: 'var(--theme-card-yellow)',
            border: '3px solid var(--theme-border)',
            boxShadow: '4px 4px 0px var(--theme-border)',
          }}
        >
          <div className="font-bold mb-3 flex items-center gap-2" style={{ color: 'var(--theme-text)' }}>
            <BookOpen className="w-5 h-5" />
            <span>{article.title}</span>
          </div>

          {/* Render article content as markdown-like text */}
          <div
            className="prose prose-sm max-w-none mb-4 p-3 rounded-lg"
            style={{
              backgroundColor: 'rgba(255, 255, 255, 0.5)',
              color: 'var(--theme-text)',
            }}
          >
            {article.content.split('\n').map((line, idx) => (
              <p key={idx} className={line.startsWith('#') ? 'font-bold' : ''}>
                {line.replace(/^#+\s*/, '')}
              </p>
            ))}
          </div>

          {/* Feedback buttons */}
          <div className="border-t pt-4" style={{ borderColor: 'var(--theme-border)' }}>
            <div className="text-center mb-3 font-medium">Did this help?</div>
            {!responded ? (
              <div className="flex gap-3">
                <button
                  onClick={handleHelpful}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-lg font-bold text-white transition-all hover:scale-105 active:scale-95"
                  style={{
                    backgroundColor: '#059669',
                    border: '2px solid #047857',
                    boxShadow: '3px 3px 0px #047857',
                  }}
                >
                  <ThumbsUp className="w-4 h-4" />
                  This helped!
                </button>
                <button
                  onClick={handleNotHelpful}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-lg font-bold text-white transition-all hover:scale-105 active:scale-95"
                  style={{
                    backgroundColor: '#dc2626',
                    border: '2px solid #b91c1c',
                    boxShadow: '3px 3px 0px #b91c1c',
                  }}
                >
                  <ThumbsDown className="w-4 h-4" />
                  Still need help
                </button>
              </div>
            ) : (
              <div className="flex items-center justify-center gap-2 font-medium" style={{ color: '#059669' }}>
                <CheckCircle className="w-4 h-4" />
                <span>Thanks for your feedback!</span>
              </div>
            )}
          </div>
        </div>
      );
    }

    return null;
  },
});

// Tool UI component for searchHelpArticles - Shows search results
const SearchHelpArticlesToolUI = makeAssistantToolUI<
  { query: string },
  { success: boolean; query: string; articles: HelpArticle[]; message: string }
>({
  toolName: 'searchHelpArticles',
  render: function SearchHelpArticlesRender({ result, status }) {
    const threadRuntime = useThreadRuntime();
    const [selectedArticle, setSelectedArticle] = React.useState<string | null>(null);
    const isLoading = status.type === 'running';

    // Handle both direct result and nested result (output.value wrapper)
    const resultData = (result as { value?: { articles?: HelpArticle[]; query?: string } })?.value || result;
    const articles = resultData?.articles || [];
    const query = resultData?.query || '';

    const handleSelectArticle = (article: HelpArticle) => {
      if (selectedArticle) return; // Prevent double-click
      setSelectedArticle(article.id);
      // Send selection message to AI
      threadRuntime.append({
        role: 'user',
        content: [{ type: 'text', text: `Show me article "${article.title}" (id: ${article.id})` }],
      });
    };

    const handleReportIssue = () => {
      threadRuntime.append({
        role: 'user',
        content: [{ type: 'text', text: 'I want to report an issue' }],
      });
    };

    // Loading state
    if (isLoading) {
      return (
        <div
          className="my-2 p-3 rounded-lg text-sm"
          style={{
            backgroundColor: '#e9d5ff',
            border: '2px solid var(--theme-border)',
          }}
        >
          <div className="flex items-center gap-2 font-bold">
            <Loader2 className="w-4 h-4 animate-spin" />
            <span>🔍 Searching articles...</span>
          </div>
        </div>
      );
    }

    // Show search results
    return (
      <div
        className="my-2 p-4 rounded-lg text-sm"
        style={{
          backgroundColor: '#e9d5ff',
          border: '3px solid var(--theme-border)',
          boxShadow: '4px 4px 0px var(--theme-border)',
        }}
      >
        <div className="font-bold mb-3 flex items-center gap-2" style={{ color: 'var(--theme-text)' }}>
          <Search className="w-5 h-5" />
          <span>Search: "{query}"</span>
        </div>

        {articles.length > 0 ? (
          <div className="flex flex-col gap-2">
            {articles.map((article) => (
              <button
                key={article.id}
                onClick={() => handleSelectArticle(article)}
                disabled={!!selectedArticle}
                className={`w-full flex items-center justify-between p-3 rounded-lg font-medium text-left transition-all ${
                  selectedArticle === article.id
                    ? 'scale-95 opacity-70'
                    : selectedArticle
                      ? 'opacity-50 cursor-not-allowed'
                      : 'hover:scale-102 active:scale-98'
                }`}
                style={{
                  backgroundColor: selectedArticle === article.id ? '#7c3aed' : 'var(--theme-card-cyan)',
                  color: selectedArticle === article.id ? 'white' : 'var(--theme-text)',
                  border: '2px solid var(--theme-border)',
                  boxShadow: selectedArticle === article.id ? 'none' : '2px 2px 0px var(--theme-border)',
                }}
              >
                <div className="flex items-center gap-2">
                  <BookOpen className="w-4 h-4 flex-shrink-0" />
                  <span className="text-sm">{article.title}</span>
                </div>
                {article.category && (
                  <span className="text-xs opacity-75 px-2 py-1 rounded" style={{ backgroundColor: 'rgba(0,0,0,0.1)' }}>
                    {article.category}
                  </span>
                )}
              </button>
            ))}
          </div>
        ) : (
          <div className="text-center py-4">
            <div className="opacity-75 mb-3">No articles found for "{query}"</div>
            <button
              onClick={handleReportIssue}
              className="px-4 py-2 rounded-lg font-bold transition-all hover:scale-105 active:scale-95"
              style={{
                backgroundColor: 'var(--theme-card-yellow)',
                color: 'var(--theme-text)',
                border: '2px solid var(--theme-border)',
                boxShadow: '2px 2px 0px var(--theme-border)',
              }}
            >
              <Bug className="w-4 h-4 inline mr-2" />
              Report an Issue
            </button>
          </div>
        )}

        {selectedArticle && (
          <div className="mt-3 flex items-center justify-center gap-2 text-sm" style={{ color: '#7c3aed' }}>
            <CheckCircle className="w-4 h-4" />
            <span>Article selected!</span>
          </div>
        )}
      </div>
    );
  },
});

// Debug component to show tool calls in message
const ToolCallsDebug: React.FC = () => {
  const message = useMessage();

  // Log message structure for debugging
  useEffect(() => {
    if (message) {
      console.log('📨 Message:', JSON.stringify(message, null, 2));
    }
  }, [message]);

  // Extract tool calls from message content
  const content = message?.content as unknown as Array<{ type: string; toolCallId?: string; toolName?: string; args?: Record<string, unknown>; result?: unknown }> | undefined;
  const toolCalls = content?.filter(part => part.type === 'tool-call') || [];

  if (toolCalls.length === 0) return null;

  return (
    <div className="mt-2 space-y-2">
      {toolCalls.map((tc, idx) => (
        <div
          key={tc.toolCallId || idx}
          className="p-2 rounded text-xs"
          style={{
            backgroundColor: 'var(--theme-surface)',
            border: '2px solid var(--theme-border)',
          }}
        >
          <div className="font-bold flex items-center gap-2">
            <span>🔧</span>
            <span>{tc.toolName || 'unknown'}</span>
          </div>
          <div className="mt-1 opacity-70">
            <strong>Args:</strong> {JSON.stringify(tc.args || {})}
          </div>
          {tc.result !== undefined && (
            <div className="mt-1">
              <strong>Result:</strong>{' '}
              <span className={(tc.result as { success?: boolean })?.success ? 'text-green-600' : 'text-red-600'}>
                {JSON.stringify(tc.result)}
              </span>
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

// Helper to strip action prefixes from message text
const stripActionPrefix = (text: string): string => {
  return text.replace(/\[ACTION:\w+\]\s*/gi, '').trim();
};

// Custom user message content that strips action prefixes
const UserMessageContent: React.FC = () => {
  const message = useMessage();

  // Extract text content from message and strip action prefixes
  const displayText = useMemo(() => {
    if (!message?.content) return '';

    const textParts = message.content
      .filter((part: { type: string }) => part.type === 'text')
      .map((part: { type: string; text?: string }) => part.text || '')
      .join(' ');

    return stripActionPrefix(textParts);
  }, [message?.content]);

  return <span>{displayText}</span>;
};

// Custom Message component with AImry avatar
const AImryMessage: React.FC = () => {
  const { user } = useAuthState();

  return (
    <MessagePrimitive.Root className="flex gap-3 mb-4">
      <MessagePrimitive.If user>
        <div className="flex gap-3 flex-row-reverse w-full">
          <div
            className="flex-shrink-0 rounded-full overflow-hidden"
            style={{
              width: '48px',
              height: '48px',
            }}
          >
            {user ? (
              <UserAvatar
                user={{
                  firstName: user.firstName,
                  lastName: user.lastName,
                  email: user.email,
                  avatarUrl: user.avatarUrl,
                }}
                size="lg"
                className="!rounded-full"
              />
            ) : (
              <div
                className="w-full h-full rounded-full flex items-center justify-center"
                style={{
                  backgroundColor: 'var(--theme-card-cyan)',
                  border: '2px solid var(--theme-border)',
                }}
              >
                <span style={{ color: 'var(--theme-text)' }}>?</span>
              </div>
            )}
          </div>
          <div
            className="px-4 py-3 max-w-[75%]"
            style={{
              backgroundColor: 'var(--theme-card-cyan)',
              color: 'var(--theme-text)',
              border: '2px solid var(--theme-border)',
              boxShadow: '3px 3px 0px var(--theme-border)',
              borderRadius: '12px 12px 0 12px',
            }}
          >
            <UserMessageContent />
          </div>
        </div>
      </MessagePrimitive.If>

      <MessagePrimitive.If assistant>
        <div className="flex gap-3 w-full">
          <div
            className="flex-shrink-0 rounded-full overflow-hidden"
            style={{
              width: '48px',
              height: '48px',
              border: '2px solid var(--theme-border)',
              boxShadow: '2px 2px 0px var(--theme-border)',
            }}
          >
            <AImryStaticAvatar size="sm" />
          </div>
          <div
            className="px-4 py-3 max-w-[85%]"
            style={{
              backgroundColor: 'var(--theme-card-pink)',
              color: 'var(--theme-text)',
              border: '2px solid var(--theme-border)',
              boxShadow: '3px 3px 0px var(--theme-border)',
              borderRadius: '12px 12px 12px 0',
            }}
          >
            <AssistantMessageContent />
            {/* Tool UIs are rendered automatically by makeAssistantToolUI */}
          </div>
        </div>
      </MessagePrimitive.If>
    </MessagePrimitive.Root>
  );
};

// Inner component that uses the chat runtime (only rendered when auth is available)
// Welcome screen with quick action buttons
interface WelcomeScreenProps {
  user: { firstName?: string } | null;
  isRTL: boolean;
}

const AiLine: React.FC<{ className?: string }> = ({ className }) => {
  const lineRef = useRef<HTMLDivElement>(null);
  const tweens = useRef<gsap.core.Tween[]>([]);

  useEffect(() => {
    const el = lineRef.current;
    if (!el) return;

    gsap.set(el, {
      '--ai-x': '50%',
      '--ai-width': '30%',
      '--ai-glow': '0.6',
    });

    function driftX() {
      tweens.current.push(
        gsap.to(el, {
          '--ai-x': gsap.utils.random(10, 90) + '%',
          duration: gsap.utils.random(1.2, 3),
          ease: 'sine.inOut',
          onComplete: driftX,
        }),
      );
    }

    function breatheWidth() {
      tweens.current.push(
        gsap.to(el, {
          '--ai-width': gsap.utils.random(15, 50) + '%',
          duration: gsap.utils.random(1, 2.5),
          ease: 'power1.inOut',
          onComplete: breatheWidth,
        }),
      );
    }

    function pulseGlow() {
      tweens.current.push(
        gsap.to(el, {
          '--ai-glow': gsap.utils.random(0.3, 1),
          duration: gsap.utils.random(0.6, 1.8),
          ease: 'power2.inOut',
          onComplete: pulseGlow,
        }),
      );
    }

    driftX();
    breatheWidth();
    pulseGlow();

    return () => {
      tweens.current.forEach((t) => t.kill());
      tweens.current = [];
    };
  }, []);

  return (
    <div
      ref={lineRef}
      className={`w-full ${className || ''}`}
      style={{
        height: '3px',
        background: `linear-gradient(
          90deg,
          transparent 0%,
          var(--theme-border, #1a1a2e) 30%,
          var(--theme-border, #1a1a2e) 70%,
          transparent 100%
        )`,
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* AI shimmer overlay */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          background: `radial-gradient(
            ellipse var(--ai-width, 30%) 100% at var(--ai-x, 50%) 50%,
            rgba(139, 92, 246, var(--ai-glow, 0.6)) 0%,
            rgba(59, 130, 246, 0.4) 40%,
            transparent 80%
          )`,
          boxShadow: '0 0 6px rgba(139, 92, 246, 0.4)',
        }}
      />
    </div>
  );
};

const WelcomeScreen: React.FC<WelcomeScreenProps> = ({ user, isRTL }) => {
  const { t } = useTranslation('common');
  const threadRuntime = useThreadRuntime();
  const bookingBtnRef = useRef<HTMLButtonElement>(null);
  useOrbShimmer(bookingBtnRef);

  const handleQuickAction = (action: 'booking' | 'help') => {
    const messages = {
      // Use action prefix for structured detection on backend
      booking: `[ACTION:BOOKING_START] ${t('aimry.quickActions.bookRoom')}`,
      help: `[action:help_start] ${t('aimry.quickActions.needHelp')}`,
    };
    threadRuntime.append({
      role: 'user',
      content: [{ type: 'text', text: messages[action] }],
    });
  };

  return (
    <div className="flex flex-col items-center justify-center py-8 text-center">
      <AImryStaticAvatar size="lg" />
      <AiLine className="-mt-[3px]" />
      <h2
        className="text-lg font-bold mt-4"
        style={{ color: 'var(--theme-text)' }}
      >
        {t('aimry.welcomeGreeting', { name: user?.firstName || '' })}
      </h2>
      <p
        className="text-sm mt-2"
        style={{ color: 'var(--theme-text-muted)' }}
      >
        {t('aimry.welcomeIntro')}
      </p>

      {/* Quick Action Buttons */}
      <div className="flex gap-3 mt-6">
        <button
          ref={bookingBtnRef}
          onClick={() => handleQuickAction('booking')}
          className="flex items-center gap-2 px-4 py-2 font-medium text-sm rounded-lg transition-all hover:scale-105 active:scale-95 calendar-quick-book-btn"
          style={{
            backgroundColor: 'var(--theme-card-cyan)',
            color: 'var(--theme-text)',
            border: '2px solid var(--theme-border)',
            boxShadow: '3px 3px 0px var(--theme-border)',
          }}
        >
          <Calendar className="w-4 h-4 relative z-[2]" />
          <span className="relative z-[2]">{t('aimry.quickActions.booking')}</span>
        </button>
        <button
          onClick={() => handleQuickAction('help')}
          className="flex items-center gap-2 px-4 py-2 font-medium text-sm rounded-lg transition-all hover:scale-105 active:scale-95"
          style={{
            backgroundColor: 'var(--theme-card-green)',
            color: 'var(--theme-text)',
            border: '2px solid var(--theme-border)',
            boxShadow: '3px 3px 0px var(--theme-border)',
          }}
        >
          <HelpCircle className="w-4 h-4" />
          {t('aimry.quickActions.help')}
        </button>
      </div>
    </div>
  );
};

// =============================================================================
// AIMRY SPEECH BUBBLE — GSAP-animated bubble rendered next to the existing bot
// =============================================================================
function SplitWords({ text }: { text: string }) {
  return (
    <>
      {text.split(' ').map((word, i) => (
        <span key={i} className="aimry-word" style={{ display: 'inline-block', whiteSpace: 'nowrap' }}>
          {word}{' '}
        </span>
      ))}
    </>
  );
}

/** Bouncing dots indicator for uploading state */
function BouncingDots() {
  return (
    <span style={{ display: 'inline-flex', gap: '3px', marginInlineStart: '4px', verticalAlign: 'middle' }}>
      {[0, 1, 2].map((i) => (
        <span
          key={i}
          style={{
            display: 'inline-block',
            width: '5px',
            height: '5px',
            borderRadius: '50%',
            background: '#000',
            animation: 'aimryDotBounce 1.2s ease-in-out infinite',
            animationDelay: `${i * 0.15}s`,
          }}
        />
      ))}
    </span>
  );
}

const AImryBubbleOverlay: React.FC<{ isRTL: boolean }> = ({ isRTL }) => {
  const { bubble } = useAImryBubble();
  const bubbleRef = useRef<HTMLDivElement>(null);
  const textRef = useRef<HTMLDivElement>(null);
  const prevStepRef = useRef(-1);

  const { isVisible, steps, currentStep } = bubble;
  const currentMessage = steps[currentStep]?.message ?? '';

  // Entrance animation
  useLayoutEffect(() => {
    if (!isVisible || !bubbleRef.current) return;
    prevStepRef.current = 0;

    const ctx = gsap.context(() => {
      const mm = gsap.matchMedia();
      mm.add(
        { small: '(max-width: 700px)', large: '(min-width: 701px)' },
        (context) => {
          const tl = gsap.timeline();
          const cond = context.conditions as { small: boolean; large: boolean };

          if (cond.large) {
            tl.fromTo(bubbleRef.current, { scale: 0, opacity: 0 }, { scale: 1, opacity: 1, duration: 0.6, ease: 'elastic.out(1, 0.5)' });
          } else {
            tl.fromTo(bubbleRef.current, { y: -20, opacity: 0, scale: 0.8 }, { y: 0, opacity: 1, scale: 1, duration: 0.45, ease: 'back.out(1.7)' });
          }

          const words = textRef.current?.children;
          if (words?.length) {
            tl.from(words, { opacity: 0, y: 12, duration: 0.3, stagger: 0.06, ease: 'back.out(2)' }, '-=0.3');
          }
        },
      );
    });

    return () => ctx.revert();
  }, [isVisible]);

  // Step transition animation
  useEffect(() => {
    if (!isVisible || currentStep === 0 || currentStep === prevStepRef.current) return;
    if (!bubbleRef.current || !textRef.current) return;
    prevStepRef.current = currentStep;

    const ctx = gsap.context(() => {
      const tl = gsap.timeline();
      tl.to(bubbleRef.current, { scale: 0.85, duration: 0.15, ease: 'power2.in' });
      tl.to(bubbleRef.current, { scale: 1, duration: 0.4, ease: 'elastic.out(1, 0.5)' });
      const words = textRef.current?.children;
      if (words?.length) {
        tl.from(words, { opacity: 0, y: 10, duration: 0.25, stagger: 0.05, ease: 'back.out(2)' }, '-=0.3');
      }
    });

    return () => ctx.revert();
  }, [isVisible, currentStep]);

  if (!isVisible || steps.length === 0) return null;

  return (
    <div
      ref={bubbleRef}
      style={{
        position: 'absolute',
        top: '100%',
        left: '50%',
        transform: 'translateX(-50%)',
        marginTop: '10px',
        background: '#F4D35E',
        border: '3px solid #000',
        borderRadius: '14px',
        padding: 'clamp(8px, 2vw, 12px) clamp(10px, 2.5vw, 16px)',
        maxWidth: 'min(200px, 60vw)',
        minWidth: '100px',
        boxShadow: '4px 4px 0 #000',
        transformOrigin: 'top center',
        zIndex: 200,
        pointerEvents: 'none',
        overflow: 'hidden',
      }}
    >
      {/* Tail pointing up toward the bot button */}
      <div
        style={{
          position: 'absolute',
          top: '-10px',
          left: '50%',
          transform: 'translateX(-50%)',
          width: 0,
          height: 0,
          borderLeft: '8px solid transparent',
          borderRight: '8px solid transparent',
          borderBottom: '10px solid #000',
        }}
      />
      <div
        style={{
          position: 'absolute',
          top: '-7px',
          left: '50%',
          transform: 'translateX(-50%)',
          width: 0,
          height: 0,
          borderLeft: '7px solid transparent',
          borderRight: '7px solid transparent',
          borderBottom: '8px solid #F4D35E',
        }}
      />
      {/* Text with word-wrap and ellipsis overflow */}
      <div
        ref={textRef}
        style={{
          fontFamily: "var(--font-pixel), monospace",
          fontSize: 'clamp(0.38rem, 1.4vw, 0.5rem)',
          color: '#000',
          textTransform: 'uppercase',
          lineHeight: '1.9',
          textAlign: 'center',
          wordBreak: 'break-word',
          overflowWrap: 'break-word',
          display: '-webkit-box',
          WebkitLineClamp: 4,
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
      >
        <SplitWords text={currentMessage} />
        <BouncingDots />
      </div>
    </div>
  );
};

const AImryAssistantInner: React.FC<{ accessToken: string }> = ({ accessToken }) => {
  const { t } = useTranslation('common');
  const isRTL = useRTL();
  const { user } = useAuthState(); // Read-only access - no side effects
  const [isOpen, setIsOpen] = useState(false);

  // Responsive: detect mobile viewport (<700px)
  const isMobile = useMediaQuery(MOBILE_BREAKPOINT);
  const { sidebarCollapsed } = useLayout();
  const sidebarWidth = isMobile ? '0px' : sidebarCollapsed ? '5rem' : '16rem';

  // Create transport with auth headers (uses streaming endpoint)
  // AssistantChatTransport is designed for @assistant-ui/react and handles multi-step tool calling
  const transport = useMemo(() => {
    console.log('🔌 Creating AssistantChatTransport with API:', `${getApiUrl()}/support/chat/stream`);
    return new AssistantChatTransport({
      api: `${getApiUrl()}/support/chat/stream`,
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    });
  }, [accessToken]);

  // Connect to backend using AI SDK runtime
  const runtime = useChatRuntime({
    transport,
  });

  return (
    <AssistantRuntimeProvider runtime={runtime}>
      {/* Register Tool UIs - 5-Step Booking Wizard */}
      <ProposeBookingToolUI />
      <CreateBookingToolUI />
      <ReportIncidentToolUI />
      <GetRoomsToolUI />
      <GetAvailableDaysToolUI />
      <GetAvailableStartTimesToolUI />
      <GetAvailableDurationsToolUI />
      <CollectBookingNoteToolUI />
      {/* Help Wizard Tool UIs */}
      <GetHelpTopicsToolUI />
      <GetHelpArticlesToolUI />
      <ShowHelpArticleToolUI />
      <SearchHelpArticlesToolUI />

      <div className="relative">
        {/* Trigger Button - Circular like other nav icons */}
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="neo-icon-btn relative flex items-center justify-center rounded-full overflow-hidden"
          style={{
            width: '56px',
            height: '56px',
            backgroundColor: 'var(--theme-surface)',
            color: 'var(--theme-text)',
          }}
          aria-label={t('aimry.aiChat')}
        >
          <img src="/img/aimry-avatar.png" alt="AImry" className="w-full h-full rounded-full object-cover" draggable={false} />
        </button>

        {/* AI indicator - animated sparkle */}
        <span
          className="absolute pointer-events-none aimry-sparkle-indicator"
          style={{
            top: '-6px',
            right: '-6px',
          }}
        >
          <Sparkles size={24} strokeWidth={1.5} className="aimry-sparkle-icon" />
        </span>

        {/* Speech bubble overlay — GSAP animated, from context */}
        <AImryBubbleOverlay isRTL={isRTL} />
      </div>

      {/* Desktop Chat Modal - Portal with sidebar offset */}
      {!isMobile && createPortal(
        <AnimatePresence>
          {isOpen && (
            <>
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 bg-black/40 backdrop-blur-sm z-[100]"
                style={{ left: sidebarWidth }}
                onClick={() => setIsOpen(false)}
              />
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                transition={{ duration: 0.15 }}
                className="fixed inset-0 flex items-center justify-center z-[101] pointer-events-none"
                style={{ left: sidebarWidth }}
              >
                <div
                  className="pointer-events-auto"
                  style={{
                    backgroundColor: COLORS.yellow,
                    border: `3px solid ${COLORS.black}`,
                    boxShadow: `6px 6px 0px ${COLORS.black}`,
                    width: '600px',
                    maxWidth: '90%',
                    maxHeight: '85vh',
                    direction: isRTL ? 'rtl' : 'ltr',
                    display: 'flex',
                    flexDirection: 'column',
                  }}
                  dir={isRTL ? 'rtl' : 'ltr'}
                  role="dialog"
                  aria-modal="true"
                  aria-label={t('aimry.aiChat')}
                >
                  {/* Header */}
                  <div
                    className="flex flex-col items-center flex-shrink-0"
                    style={{
                      backgroundColor: COLORS.lightBlue,
                      borderBottom: `2px solid ${COLORS.black}`,
                    }}
                  >
                    <div className="flex items-center justify-center relative w-full p-3">
                      <span
                        className="absolute text-xs flex items-center gap-1"
                        style={{ [isRTL ? 'right' : 'left']: 12, color: COLORS.black, opacity: 0.7 }}
                      >
                        <span
                          className="w-2 h-2 rounded-full"
                          style={{ backgroundColor: '#4ADE80' }}
                        />
                        {t('aimry.statusOnline')}
                      </span>
                      <h3
                        style={{
                          color: COLORS.black,
                          fontSize: 18,
                          fontWeight: 700,
                          letterSpacing: 1,
                          textTransform: 'uppercase',
                        }}
                      >
                        AImry
                      </h3>
                      <button
                        onClick={() => setIsOpen(false)}
                        className="absolute flex items-center justify-center w-8 h-8 rounded-full transition-transform active:scale-95"
                        style={{
                          [isRTL ? 'left' : 'right']: 12,
                          backgroundColor: COLORS.pink,
                          border: `2px solid ${COLORS.black}`,
                          boxShadow: `2px 2px 0px ${COLORS.black}`,
                        }}
                      >
                        <XIcon className="w-4 h-4" style={{ color: COLORS.black }} />
                      </button>
                    </div>
                  </div>

                  {/* Messages Thread */}
                  <ThreadPrimitive.Root
                    style={{
                      backgroundColor: COLORS.yellow,
                      flex: 1,
                      minHeight: '400px',
                      maxHeight: '520px',
                      display: 'flex',
                      flexDirection: 'column',
                    }}
                  >
                    <ThreadPrimitive.Viewport
                      className="p-4 hide-scrollbar"
                      autoScroll
                      style={{
                        flex: 1,
                        overflow: 'auto',
                        scrollBehavior: 'smooth',
                      }}
                    >
                      {/* Welcome message */}
                      <ThreadPrimitive.Empty>
                        <WelcomeScreen user={user} isRTL={isRTL} />
                      </ThreadPrimitive.Empty>

                      <ThreadPrimitive.Messages components={{ Message: AImryMessage }} />
                    </ThreadPrimitive.Viewport>
                  </ThreadPrimitive.Root>

                  {/* Composer (Input) */}
                  <ComposerPrimitive.Root
                    className="p-3 flex gap-2"
                    style={{
                      backgroundColor: COLORS.yellow,
                      borderTop: `2px solid ${COLORS.black}`,
                    }}
                  >
                    <ComposerPrimitive.Input
                      placeholder={t('aimry.placeholder')}
                      className="flex-1 px-4 py-3 text-sm rounded-lg"
                      style={{
                        backgroundColor: 'white',
                        color: COLORS.black,
                        border: `2px solid ${COLORS.black}`,
                        outline: 'none',
                      }}
                    />
                    <ComposerPrimitive.Send
                      className="p-3 rounded-lg"
                      style={{
                        backgroundColor: COLORS.lightBlue,
                        color: COLORS.black,
                        border: `2px solid ${COLORS.black}`,
                        boxShadow: `2px 2px 0px ${COLORS.black}`,
                      }}
                    >
                      <SendIcon className="w-4 h-4" />
                    </ComposerPrimitive.Send>
                  </ComposerPrimitive.Root>

                </div>
              </motion.div>
            </>
          )}
        </AnimatePresence>,
        document.body
      )}

      {/* Mobile Full-Screen Drawer - Rendered via Portal to escape stacking context */}
      {isMobile && createPortal(
        <AnimatePresence>
          {isOpen && (
            <>
              {/* Backdrop Overlay - Ensures full coverage over bottom nav */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 z-[100] bg-black/60"
                onClick={() => setIsOpen(false)}
                aria-hidden="true"
              />

            {/* Drawer Content */}
            <motion.div
              initial={{ y: '100%' }}
              animate={{ y: 0 }}
              exit={{ y: '100%' }}
              drag="y"
              dragConstraints={{ top: 0 }}
              dragElastic={0.2}
              onDragEnd={(_, info) => {
                // Swipe down to dismiss (if dragged more than 100px down)
                if (info.offset.y > 100) {
                  setIsOpen(false);
                }
              }}
              transition={{ type: 'spring', damping: 25, stiffness: 200 }}
              className="fixed inset-0 z-[101] flex flex-col"
              dir={isRTL ? 'rtl' : 'ltr'}
              role="dialog"
              aria-modal="true"
              aria-label={t('aimry.aiChat')}
              style={{
                height: '100dvh', // Dynamic viewport height - handles keyboard
                backgroundColor: COLORS.yellow,
              }}
            >
            {/* Header (includes drag handle) */}
            <div
              className="flex flex-col items-center flex-shrink-0"
              style={{
                backgroundColor: COLORS.lightBlue,
                borderBottom: `4px solid ${COLORS.black}`,
                paddingTop: 'env(safe-area-inset-top, 0.5rem)',
              }}
            >
              {/* Drag Handle */}
              <div className="pt-2 pb-1">
                <div
                  className="w-12 h-1.5 rounded-full"
                  style={{ backgroundColor: COLORS.black, opacity: 0.3 }}
                />
              </div>
              {/* Title + Close */}
              <div className="flex items-center justify-center relative w-full p-3">
                <span
                  className="absolute text-xs flex items-center gap-1"
                  style={{ [isRTL ? 'right' : 'left']: 12, color: COLORS.black, opacity: 0.7 }}
                >
                  <span
                    className="w-2 h-2 rounded-full"
                    style={{ backgroundColor: '#4ADE80' }}
                  />
                  {t('aimry.statusOnline')}
                </span>
                <h2
                  style={{
                    color: COLORS.black,
                    fontSize: 18,
                    fontWeight: 700,
                    letterSpacing: 1,
                    textTransform: 'uppercase',
                  }}
                >
                  AImry
                </h2>
                <button
                  onClick={() => setIsOpen(false)}
                  className="absolute flex items-center justify-center w-8 h-8 rounded-full transition-transform active:scale-95"
                  style={{
                    [isRTL ? 'left' : 'right']: 12,
                    backgroundColor: COLORS.pink,
                    border: `2px solid ${COLORS.black}`,
                    boxShadow: `2px 2px 0px ${COLORS.black}`,
                  }}
                  aria-label={t('aimry.closeChat')}
                >
                  <XIcon className="h-4 w-4" style={{ color: COLORS.black }} />
                </button>
              </div>
            </div>

            {/* Messages Thread - Scrollable area */}
            <ThreadPrimitive.Root
              className="flex-1 flex flex-col overflow-hidden"
              style={{
                backgroundColor: COLORS.tan,
                minHeight: 0, // Critical for flex overflow
              }}
            >
              <ThreadPrimitive.Viewport
                className="flex-1 p-4 hide-scrollbar"
                autoScroll
                style={{
                  overflow: 'auto',
                  overscrollBehaviorY: 'contain', // Prevents scroll chaining
                  minHeight: 0,
                }}
                role="log"
                aria-live="polite"
              >
                {/* Welcome message */}
                <ThreadPrimitive.Empty>
                  <WelcomeScreen user={user} isRTL={isRTL} />
                </ThreadPrimitive.Empty>

                <ThreadPrimitive.Messages components={{ Message: AImryMessage }} />
              </ThreadPrimitive.Viewport>
            </ThreadPrimitive.Root>

            {/* Composer (Input) - Fixed at bottom with safe area */}
            <div
              className="flex-shrink-0"
              style={{
                backgroundColor: COLORS.lightBlue,
                borderTop: `4px solid ${COLORS.black}`,
                padding: `1rem 1rem calc(1rem + env(safe-area-inset-bottom))`,
              }}
            >
              <ComposerPrimitive.Root className="flex gap-3 items-end">
                <ComposerPrimitive.Input
                  placeholder={t('aimry.writePlaceholder')}
                  rows={1}
                  autoFocus={false}
                  className="flex-1 px-4 py-3 text-base rounded-xl resize-none focus:outline-none min-h-[48px] max-h-[120px]"
                  style={{
                    backgroundColor: 'white',
                    color: COLORS.black,
                    border: `3px solid ${COLORS.black}`,
                  }}
                />
                <ComposerPrimitive.Send
                  className="rounded-xl flex-shrink-0 flex items-center justify-center transition-all active:translate-y-0.5"
                  style={{
                    width: '52px',
                    height: '52px',
                    backgroundColor: COLORS.pink,
                    color: COLORS.black,
                    border: `3px solid ${COLORS.black}`,
                    boxShadow: `4px 4px 0px ${COLORS.black}`,
                  }}
                  aria-label={t('aimry.send')}
                >
                  <SendIcon className="w-6 h-6" />
                </ComposerPrimitive.Send>
              </ComposerPrimitive.Root>
            </div>
          </motion.div>
          </>
          )}
        </AnimatePresence>,
        document.body
      )}
    </AssistantRuntimeProvider>
  );
};

// Main AImry Assistant Component - Wrapper that checks for auth
export const AImryAssistant: React.FC = () => {
  const { t } = useTranslation('common');
  const { accessToken } = useAuthState(); // Read-only access - no side effects

  // Debug: Log token state
  console.log('🔐 AImry Auth check:', { hasToken: !!accessToken, tokenPreview: accessToken ? accessToken.substring(0, 20) + '...' : 'null' });

  // Don't render the chat if there's no token
  if (!accessToken) {
    return (
      <div className="relative">
        {/* Trigger Button - Disabled state when not authenticated (circular like other nav icons) */}
        <button
          disabled
          className="neo-icon-btn relative flex items-center justify-center rounded-full overflow-hidden opacity-50 cursor-not-allowed"
          style={{
            width: '56px',
            height: '56px',
            backgroundColor: 'var(--theme-surface)',
            color: 'var(--theme-text)',
          }}
          aria-label={t('aimry.loginRequired')}
        >
          <img src="/img/aimry-avatar.png" alt="AImry" className="w-full h-full rounded-full object-cover" draggable={false} />
        </button>
      </div>
    );
  }

  // Render the inner component only when authenticated
  // Use key to force remount when token changes (ensures fresh transport)
  return <AImryAssistantInner key={accessToken.substring(0, 10)} accessToken={accessToken} />;
};

export default AImryAssistant;
